import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { specialMotoAPI, vehicleRegistrationAPI } from '../services/api';
import SpecialMotoForm from './SpecialMotoForm';
import './SpecialMoto.css';

const CONDITION_LABELS = {
  QUY_TRINH: 'Quy trình mất hồ sơ gốc',
  BAN_CHINH: 'Bản chính / Bản sao công chứng',
  BAN_PHO_TO: 'Bản phô tô',
  KHAC: 'Khác'
};

const DOC_STATUS_LABELS = {
  hop_le: 'Hợp lệ',
  khong_hop_le: 'Không hợp lệ'
};

const SpecialMotoManagement = ({ user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [units, setUnits] = useState([]);
  const [quickSearchInput, setQuickSearchInput] = useState('');

  // ── Applied filters (dùng để fetch) ──
  const [activeFilters, setActiveFilters] = useState({
    search: '',
    unitId: '',
    documentStatus: '',
    condition: '',
    manufactureYearFrom: '',
    manufactureYearTo: '',
    registrationDateFrom: '',
    registrationDateTo: ''
  });

  // ── Draft filters trong modal (chưa apply) ──
  const defaultDraft = {
    search: '',
    unitId: '',
    documentStatus: '',
    condition: '',
    manufactureYearFrom: '',
    manufactureYearTo: '',
    registrationDateFrom: '',
    registrationDateTo: ''
  };
  const [draftFilters, setDraftFilters] = useState(defaultDraft);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importUnit, setImportUnit] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Export
  const [exporting, setExporting] = useState(false);

  const isAdmin = user && user.role === 'admin';

  const canEdit = (row) => {
    if (isAdmin) return true;
    const uId = (user && user.unitId) || (user && user.unit && user.unit.id);
    if (uId && String(row.unitId) === String(uId)) return true;
    return false;
  };

  // Đếm số filter đang active
  const activeFilterCount = Object.values(activeFilters).filter(v => v !== '').length;

  useEffect(() => {
    fetchData();
  }, [pagination.page, activeFilters]);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    setQuickSearchInput(activeFilters.search || '');
  }, [activeFilters.search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(activeFilters.search && { search: activeFilters.search }),
        ...(activeFilters.unitId && { unitId: activeFilters.unitId }),
        ...(activeFilters.documentStatus && { documentStatus: activeFilters.documentStatus }),
        ...(activeFilters.condition && { condition: activeFilters.condition }),
        ...(activeFilters.manufactureYearFrom && { manufactureYearFrom: activeFilters.manufactureYearFrom }),
        ...(activeFilters.manufactureYearTo && { manufactureYearTo: activeFilters.manufactureYearTo }),
        ...(activeFilters.registrationDateFrom && { registrationDateFrom: activeFilters.registrationDateFrom }),
        ...(activeFilters.registrationDateTo && { registrationDateTo: activeFilters.registrationDateTo })
      };
      const res = await specialMotoAPI.getAll(params);
      setData(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination.total,
        totalPages: res.data.pagination.totalPages
      }));
    } catch (err) {
      console.error(err);
      Swal.fire('Lỗi', 'Không thể tải danh sách xe máy CĐ', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await specialMotoAPI.getSupervisorUnits();
      setUnits(res.data || []);
    } catch (err) {
      console.error('Error fetch units:', err);
    }
  };

  // Mở modal filter và copy activeFilters vào draft
  const handleOpenFilter = () => {
    setDraftFilters({ ...activeFilters });
    setShowFilterModal(true);
  };

  // Áp dụng filter từ draft
  const handleApplyFilter = () => {
    setActiveFilters({ ...draftFilters });
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowFilterModal(false);
  };

  // Reset tất cả filter
  const handleResetFilter = () => {
    setDraftFilters(defaultDraft);
  };

  const handleClearAllFilters = () => {
    setActiveFilters(defaultDraft);
    setDraftFilters(defaultDraft);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // ── Debounce cho tìm kiếm nhanh (Real-time) ──
  useEffect(() => {
    const handler = setTimeout(() => {
      if (quickSearchInput !== activeFilters.search) {
        setActiveFilters(prev => ({ ...prev, search: quickSearchInput }));
        setDraftFilters(prev => ({ ...prev, search: quickSearchInput }));
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500); // Đợi 500ms sau khi ngừng gõ mới call API
    return () => clearTimeout(handler);
  }, [quickSearchInput, activeFilters.search]);

  const handleDraftChange = (field, value) => {
    setDraftFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };


  // ── Add / Edit ──
  const handleOpenAdd = () => {
    setEditingItem(null);
    setShowFormModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setShowFormModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await specialMotoAPI.update(editingItem.id, formData);
        Swal.fire({ icon: 'success', title: 'Đã cập nhật', timer: 1500, showConfirmButton: false });
      } else {
        await specialMotoAPI.create(formData);
        Swal.fire({ icon: 'success', title: 'Đã thêm mới', timer: 1500, showConfirmButton: false });
      }
      setShowFormModal(false);
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchData();
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Có lỗi xảy ra khi lưu');
    }
  };

  // ── Delete ──
  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Xác nhận xóa?',
      html: `Bạn có chắc muốn xóa bản ghi biển số <b>${item.licensePlate}</b>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonText: 'Hủy',
      confirmButtonText: 'Xóa'
    });
    if (!result.isConfirmed) return;
    try {
      await specialMotoAPI.remove(item.id);
      Swal.fire({ icon: 'success', title: 'Đã xóa', timer: 1500, showConfirmButton: false });
      fetchData();
    } catch (err) {
      Swal.fire('Lỗi', err.response?.data?.message || 'Không thể xóa', 'error');
    }
  };

  // ── Import Excel ──
  const handleImport = async () => {
    if (!importFile) {
      Swal.fire('Thông báo', 'Vui lòng chọn file Excel', 'warning');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      if (importUnit) fd.append('unitId', importUnit);
      const res = await specialMotoAPI.importExcel(fd);
      setImportResult({ type: 'success', data: res.data });
      fetchData();
    } catch (err) {
      setImportResult({
        type: 'error',
        message: err.response?.data?.message || 'Lỗi khi import file'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImport = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setImportUnit('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Export Excel ──
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await specialMotoAPI.exportExcel(activeFilters);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'danh-sach-xe-may-cd.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Lỗi', 'Không thể xuất Excel', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Format condition ──
  const formatCondition = (condition, detail) => {
    if (!condition) return <span className="badge badge-secondary">—</span>;
    const label = CONDITION_LABELS[condition] || condition;
    if (condition === 'KHAC') {
      return (
        <span className="badge badge-warning" title={detail}>
          Khác{detail ? `: ${detail.length > 30 ? detail.substring(0, 30) + '...' : detail}` : ''}
        </span>
      );
    }
    const colorMap = {
      QUY_TRINH: 'badge-danger',
      BAN_CHINH: 'badge-success',
      BAN_PHO_TO: 'badge-info'
    };
    return <span className={`badge ${colorMap[condition] || 'badge-secondary'}`}>{label}</span>;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('vi-VN');
  };

  // Page numbers
  const renderPageNumbers = () => {
    const { page, totalPages } = pagination;
    const pages = [];
    const range = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - range && i <= page + range)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  return (
    <div className="special-moto-container">

      {/* ── Toolbar ── */}
      <div className="sm-toolbar">
        {/* Thanh tìm kiếm nhanh */}
        <input
          className="sm-search-input"
          placeholder="Tìm biển số, tên chủ, số khung..."
          value={quickSearchInput}
          onChange={e => setQuickSearchInput(e.target.value)}
        />

        {/* Nút Lọc — mở modal filter */}
        <button className="sm-btn sm-btn-filter" onClick={handleOpenFilter}>
          Lọc / Tìm kiếm
          {activeFilterCount > 0 && (
            <span className="sm-filter-badge">{activeFilterCount}</span>
          )}
        </button>

        {/* Xóa filter nếu đang có */}
        {activeFilterCount > 0 && (
          <button className="sm-btn sm-btn-secondary" onClick={handleClearAllFilters}>
            Bỏ lọc
          </button>
        )}

        {/* Export */}
        <button
          className="sm-btn sm-btn-success"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Đang xuất...' : 'Xuất Excel'}
        </button>

        {/* Import – chỉ admin */}
        {isAdmin && (
          <button
            className="sm-btn sm-btn-warning"
            onClick={() => { setShowImportModal(true); setImportResult(null); }}
          >
            Import Excel
          </button>
        )}

        {/* Thêm mới – admin + supervisor */}
        <button className="sm-btn sm-btn-primary" onClick={handleOpenAdd}>
          Thêm mới
        </button>
      </div>

      {/* ── Active filter tags ── */}
      {activeFilterCount > 0 && (
        <div className="sm-active-filters">
          {activeFilters.search && (
            <span className="sm-filter-tag">Tìm: "{activeFilters.search}"</span>
          )}
          {activeFilters.documentStatus && (
            <span className="sm-filter-tag">
              Hồ sơ: {activeFilters.documentStatus === 'hop_le' ? 'Hợp lệ' : 'Không hợp lệ'}
            </span>
          )}
          {activeFilters.condition && (
            <span className="sm-filter-tag">
              Tình trạng: {CONDITION_LABELS[activeFilters.condition] || activeFilters.condition}
            </span>
          )}
          {activeFilters.unitId && (
            <span className="sm-filter-tag">
              Đơn vị: {units.find(u => String(u.id) === String(activeFilters.unitId))?.name || activeFilters.unitId}
            </span>
          )}
          {(activeFilters.manufactureYearFrom || activeFilters.manufactureYearTo) && (
            <span className="sm-filter-tag">
              Năm SX: {activeFilters.manufactureYearFrom || '...'} – {activeFilters.manufactureYearTo || '...'}
            </span>
          )}
          {(activeFilters.registrationDateFrom || activeFilters.registrationDateTo) && (
            <span className="sm-filter-tag">
              Ngày ĐK: {activeFilters.registrationDateFrom || '...'} – {activeFilters.registrationDateTo || '...'}
            </span>
          )}
        </div>
      )}

      {/* ── Stats bar ── */}
      <div className="sm-stats-bar">
        <div className="sm-stat-card">
          <div className="stat-value">{pagination.total}</div>
          <div className="stat-label">Tổng bản ghi</div>
        </div>
        <div className="sm-stat-card">
          <div className="stat-value">{data.filter(d => d.documentStatus === 'hop_le').length}</div>
          <div className="stat-label">Hợp lệ (trang này)</div>
        </div>
        <div className="sm-stat-card">
          <div className="stat-value">{data.filter(d => d.documentStatus === 'khong_hop_le').length}</div>
          <div className="stat-label">Không hợp lệ (trang này)</div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="sm-table-wrapper">
        {loading ? (
          <div className="sm-loading">⏳ Đang tải dữ liệu...</div>
        ) : data.length === 0 ? (
          <div className="sm-empty">
            <div style={{ fontSize: 40 }}>📋</div>
            <p>Chưa có dữ liệu. Hãy thêm mới hoặc import từ Excel.</p>
          </div>
        ) : (
          <table className="sm-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Biển số</th>
                <th>Ngày ĐK</th>
                <th>Tên chủ PT</th>
                <th>Địa chỉ</th>
                <th>Loại xe CD</th>
                <th>Nhãn hiệu</th>
                <th>Số ĐC</th>
                <th>Số khung</th>
                <th>Năm SX</th>
                <th>Loại hồ sơ</th>
                <th>Tình trạng</th>
                <th>Người tạo</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const stt = (pagination.page - 1) * pagination.limit + idx + 1;
                return (
                  <tr key={row.id}>
                    <td className="stt">{stt}</td>
                    <td style={{ fontWeight: 700, color: '#1a237e' }}>{row.licensePlate}</td>
                    <td className="center">{formatDate(row.registrationDate)}</td>
                    <td>{row.ownerName || '—'}</td>
                    <td style={{ maxWidth: 160, wordBreak: 'break-word' }}>{row.ownerAddress || '—'}</td>
                    <td>{row.vehicleType || '—'}</td>
                    <td>{row.brand || '—'}</td>
                    <td>{row.engineNumber || '—'}</td>
                    <td>{row.chassisNumber || '—'}</td>
                    <td className="center">{row.manufactureYear || '—'}</td>
                    <td className="center">
                      {row.documentStatus ? (
                        <span className={`badge ${row.documentStatus === 'hop_le' ? 'badge-success' : 'badge-danger'}`}>
                          {DOC_STATUS_LABELS[row.documentStatus]}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{formatCondition(row.condition, row.conditionDetail)}</td>
                    <td style={{ fontSize: 12, color: '#607d8b' }}>
                      {row.creator ? row.creator.username : '—'}
                    </td>
                    <td>
                      <div className="sm-row-actions">
                        {canEdit(row) ? (
                          <button
                            className="sm-icon-btn sm-icon-btn-edit"
                            title="Chỉnh sửa"
                            onClick={() => handleOpenEdit(row)}
                          >✏️</button>
                        ) : (
                          <span style={{ color: '#b0bec5', fontSize: 12 }}>—</span>
                        )}
                        {canEdit(row) && (
                          <button
                            className="sm-icon-btn sm-icon-btn-del"
                            title="Xóa"
                            onClick={() => handleDelete(row)}
                          >🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {pagination.totalPages > 1 && (
        <div className="sm-pagination">
          <button
            className="sm-page-btn"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >‹</button>

          {renderPageNumbers().map((p, i) => (
            p === '...'
              ? <span key={`e-${i}`} className="sm-page-info">...</span>
              : <button
                key={p}
                className={`sm-page-btn ${pagination.page === p ? 'active' : ''}`}
                onClick={() => handlePageChange(p)}
              >{p}</button>
          ))}

          <button
            className="sm-page-btn"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
          >›</button>

          <span className="sm-page-info">
            Trang {pagination.page}/{pagination.totalPages} — {pagination.total} bản ghi
          </span>
        </div>
      )}

      {/* ══════════════════════════════
           MODAL: FORM THÊM / SỬA
         ══════════════════════════════ */}
      {showFormModal && (
        <div className="sm-modal-overlay" onClick={() => setShowFormModal(false)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <div className="sm-modal-header">
              <h3>{editingItem ? 'Chỉnh sửa bản ghi' : 'Thêm mới xe máy CD'}</h3>
              <button className="sm-modal-close" onClick={() => setShowFormModal(false)}>✕</button>
            </div>
            <div className="sm-modal-body">
              <SpecialMotoForm
                initialData={editingItem}
                onSave={handleSave}
                onCancel={() => setShowFormModal(false)}
                user={user}
                units={units}
              />
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
           MODAL: IMPORT EXCEL
         ══════════════════════════════ */}
      {showImportModal && (
        <div className="sm-modal-overlay" onClick={handleCloseImport}>
          <div className="sm-modal sm-import-modal" onClick={e => e.stopPropagation()}>
            <div className="sm-modal-header">
              <h3>Import danh sách từ Excel</h3>
              <button className="sm-modal-close" onClick={handleCloseImport}>✕</button>
            </div>
            <div className="sm-modal-body">
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#546e7a' }}>
                File Excel cần có các cột: <b>STT, Biển số, Ngày đăng ký, Tên chủ phương tiện, Địa chỉ, Loại xe máy chuyên dùng, Nhãn hiệu, Số động cơ, Số khung, Năm sản xuất, Loại hồ sơ, Tình trạng</b>
              </p>

              {/* Chọn đơn vị (admin có thể chọn) */}
              {units.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#37474f', display: 'block', marginBottom: 4 }}>
                    Đơn vị (tùy chọn)
                  </label>
                  <select
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: '1.5px solid #c5cae9', fontSize: 14, outline: 'none'
                    }}
                    value={importUnit}
                    onChange={e => setImportUnit(e.target.value)}
                  >
                    <option value="">-- Chọn đơn vị --</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}

              {/* Drop zone */}
              <div
                className="sm-file-zone"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <div style={{ fontSize: 36 }}>📂</div>
                <p>Nhấn để chọn file Excel (.xlsx, .xls)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={e => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                />
              </div>

              {importFile && (
                <div className="sm-file-name">📄 {importFile.name}</div>
              )}

              {/* Import result */}
              {importResult && (
                <div className={`sm-import-result ${importResult.type}`}>
                  {importResult.type === 'success' ? (
                    <>
                      <b>✅ {importResult.data.message}</b>
                      {importResult.data.errors && importResult.data.errors.length > 0 && (
                        <ul>
                          {importResult.data.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      )}
                    </>
                  ) : (
                    <b>❌ {importResult.message}</b>
                  )}
                </div>
              )}
            </div>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={handleCloseImport}>Đóng</button>
              <button
                className="sm-btn sm-btn-primary"
                onClick={handleImport}
                disabled={importing || !importFile}
              >
                {importing ? 'Đang import...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
           MODAL: LỌC / TÌM KIẾM NÂNG CAO
         ══════════════════════════════ */}
      {showFilterModal && (
        <div className="sm-modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="sm-modal sm-filter-modal" onClick={e => e.stopPropagation()}>
            <div className="sm-modal-header">
              <h3>Lọc / Tìm kiếm nâng cao</h3>
              <button className="sm-modal-close" onClick={() => setShowFilterModal(false)}>✕</button>
            </div>
            <div className="sm-modal-body">
              <div className="sm-filter-grid">

                {/* Tìm kiếm text */}
                <div className="sm-filter-section" style={{ gridColumn: '1 / -1' }}>
                  <label className="sm-filter-label">Tìm kiếm (biển số, tên chủ, số khung, số động cơ...)</label>
                  <input
                    className="sm-filter-input"
                    placeholder="Nhập từ khoá tìm kiếm..."
                    value={draftFilters.search}
                    onChange={e => handleDraftChange('search', e.target.value)}
                  />
                </div>

                {/* Loại hồ sơ */}
                <div className="sm-filter-section">
                  <label className="sm-filter-label">Loại hồ sơ</label>
                  <div className="sm-filter-options">
                    {[
                      { value: '', label: 'Tất cả' },
                      { value: 'hop_le', label: 'Hợp lệ' },
                      { value: 'khong_hop_le', label: 'Không hợp lệ' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={`sm-filter-opt-btn ${draftFilters.documentStatus === opt.value ? 'selected' : ''}`}
                        onClick={() => handleDraftChange('documentStatus', opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tình trạng */}
                <div className="sm-filter-section">
                  <label className="sm-filter-label">Tình trạng</label>
                  <div className="sm-filter-options" style={{ flexDirection: 'column' }}>
                    {[
                      { value: '', label: 'Tất cả' },
                      { value: 'QUY_TRINH', label: 'Quy trình mất hồ sơ gốc' },
                      { value: 'BAN_CHINH', label: 'Bản chính / công chứng' },
                      { value: 'BAN_PHO_TO', label: 'Bản phô tô' },
                      { value: 'KHAC', label: 'Khác' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={`sm-filter-opt-btn ${draftFilters.condition === opt.value ? 'selected' : ''}`}
                        onClick={() => handleDraftChange('condition', opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Năm sản xuất */}
                <div className="sm-filter-section">
                  <label className="sm-filter-label">Năm sản xuất</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      className="sm-filter-input"
                      placeholder="Từ năm"
                      min="1900" max="2030"
                      value={draftFilters.manufactureYearFrom}
                      onChange={e => handleDraftChange('manufactureYearFrom', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#90a4ae', fontWeight: 600 }}>—</span>
                    <input
                      type="number"
                      className="sm-filter-input"
                      placeholder="Đến năm"
                      min="1900" max="2030"
                      value={draftFilters.manufactureYearTo}
                      onChange={e => handleDraftChange('manufactureYearTo', e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                {/* Ngày đăng ký */}
                <div className="sm-filter-section">
                  <label className="sm-filter-label">Ngày đăng ký</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="date"
                      className="sm-filter-input"
                      value={draftFilters.registrationDateFrom}
                      onChange={e => handleDraftChange('registrationDateFrom', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#90a4ae', fontWeight: 600 }}>—</span>
                    <input
                      type="date"
                      className="sm-filter-input"
                      value={draftFilters.registrationDateTo}
                      onChange={e => handleDraftChange('registrationDateTo', e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                {/* Đơn vị */}
                {units.length > 0 && (
                  <div className="sm-filter-section" style={{ gridColumn: '1 / -1' }}>
                    <label className="sm-filter-label">Đơn vị</label>
                    <select
                      className="sm-filter-input"
                      value={draftFilters.unitId}
                      onChange={e => handleDraftChange('unitId', e.target.value)}
                    >
                      <option value="">-- Tất cả đơn vị --</option>
                      {units.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}

              </div>
            </div>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={handleResetFilter}>
                Đặt lại
              </button>
              <button className="sm-btn sm-btn-secondary" onClick={() => setShowFilterModal(false)}>
                Hủy
              </button>
              <button className="sm-btn sm-btn-primary" onClick={handleApplyFilter}>
                Áp dụng lọc
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialMotoManagement;
